import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { aboutContent } from '../data/mockData';

const About = () => {
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email')
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        document.getElementById('form-fields').style.display = 'none';
        document.getElementById('success-message').style.display = 'block';
        e.target.reset();
        setTimeout(() => {
          document.getElementById('form-fields').style.display = 'block';
          document.getElementById('success-message').style.display = 'none';
        }, 5000);
      } else {
        alert('Failed to sign up. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      alert('Failed to sign up. Please try again.');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Quote and Image */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Content */}
            <div>
              <h1 className="text-gold text-3xl md:text-4xl lg:text-5xl font-bold tracking-wider uppercase mb-8">
                {aboutContent.hero.tagline}
              </h1>
              <blockquote className="font-cormorant-garamond text-3xl md:text-4xl lg:text-5xl text-navy italic leading-relaxed hover-text-zoom mb-10">
                {aboutContent.hero.quote}
              </blockquote>
              <Button
                asChild
                className="bg-gold hover:bg-[#e09f0a] text-white font-bold px-8 py-6 text-sm tracking-wide rounded-md transition-colors"
              >
                <a href="/contact">FIND OUT MORE</a>
              </Button>
            </div>

            {/* Right Image */}
            <div className="hover-zoom max-w-md mx-auto lg:max-w-lg">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src={aboutContent.hero.image}
                  alt="Maryka Groenewald serving community"
                  className="w-full h-auto object-cover transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Banner */}
      <section className="py-10 px-6 lg:px-12 bg-gold">
        <div className="container mx-auto text-center">
          <blockquote className="font-cormorant-garamond font-semibold text-xl md:text-2xl lg:text-3xl text-gray-700 italic max-w-3xl mx-auto leading-relaxed">
            &ldquo;{aboutContent.values.quote}&rdquo;
          </blockquote>
        </div>
      </section>

      {/* A Blessed Childhood Section */}
      <section className="py-20 px-6 lg:px-12 bg-cream">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Image */}
            <div className="hover-zoom max-w-sm mx-auto lg:max-w-md">
              <div className="rounded-3xl overflow-hidden shadow-xl border-8 border-white">
                <img
                  src={aboutContent.sections[0].image}
                  alt={aboutContent.sections[0].title}
                  className="w-full h-auto object-cover transition-transform duration-300"
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <h3 className="font-cormorant-garamond text-4xl md:text-5xl text-navy italic hover-text-zoom">
                {aboutContent.sections[0].subtitle}
              </h3>
              <div className="mt-6 text-gray-600 leading-relaxed text-lg space-y-4">
                <p>I was born in Johannesburg, South Africa, and alongside my parents, raised by two absolute gems — my grandma and granddad (or as I knew them, Oumie and Oupie).</p>
                <p>When I was 13, my parents, my sister and I packed up our lives and moved to Western Australia. New country, new school, new accent issues — the whole lot. Thankfully, I landed in a supportive school community with a wonderful chaplain, and a solid church community around us. It made a big difference while we found our feet.</p>
                <p>I later studied Community Development and Politics at Murdoch University, and that is really where it all started…</p>
                <p>Over the years, I mentored young people, supported children in foster care, and stood alongside women leaving exploitation and trafficking. I helped build volunteer programs, strengthen local communities, and design a school-engagement program that reached dozens of schools across WA, the Northern Territory and South Australia.</p>
                <p>I also served with a team in prisons, coordinating restorative justice programs and helping with the Angel Tree Christmas Project, which provides Christmas gifts for children with a mum or dad in prison.</p>
                <p>Those years of service challenged and shape me immensely – and I loved it!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Road Ahead Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Content */}
            <div>
              <span className="block text-gold text-sm md:text-base font-bold tracking-widest uppercase">
                THE ROAD AHEAD
              </span>
              <h3 className="mt-4 font-cormorant-garamond text-4xl md:text-5xl text-navy italic hover-text-zoom">
                {aboutContent.sections[1].subtitle}
              </h3>
              <div className="mt-6 text-gray-600 leading-relaxed text-lg space-y-4">
                <p>In 2016, I became State Director of The Australian Christians Party (AC). For almost a decade I built the organisation, trained volunteers, and gave conservative Christians a clear, respectful voice in WA.</p>
                <p>In 2025 I was elected to the Legislative Council, the first Australian Christians MP in any parliament. It's a privilege to lead the Party.</p>
                <p className="font-bold text-navy">We stand for life, faith, family and freedom. That means:</p>
              </div>
              <ul className="mt-4 space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="text-gold mr-3 text-xl">●</span>
                  <span>Celebrating freedom of expression and speech</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 text-xl">●</span>
                  <span>Defending life in all its stages</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 text-xl">●</span>
                  <span>Protecting children from life-altering decisions and gender ideology in law and medicine</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 text-xl">●</span>
                  <span>Defending parents' rights and faith-based schools</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 text-xl">●</span>
                  <span>Advocating for honest budgets, fairer tax for families and safer communities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3 text-xl">●</span>
                  <span>Fighting for our regional and remote communities to ensure the best outcomes for all families and all children</span>
                </li>
              </ul>
              <blockquote className="mt-8 border-l-4 border-gold pl-6 italic text-gray-600 text-lg">
                <p>My approach is simple: walk the walk, do the work. It's the greatest honour to be serving Western Australians with a clear aim —to protect life, strengthen families and secure our freedoms.</p>
              </blockquote>
              <Button
                asChild
                className="mt-8 bg-gold hover:bg-[#e09f0a] text-white font-bold px-8 py-6 text-sm tracking-wide rounded-md transition-colors"
              >
                <a href="/contact">FIND OUT MORE</a>
              </Button>
            </div>

            {/* Image */}
            <div className="hover-zoom max-w-sm mx-auto lg:max-w-md">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src={aboutContent.sections[1].image}
                  alt={aboutContent.sections[1].title}
                  className="w-full h-auto object-cover transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup Section */}
      <section className="py-20 px-6 lg:px-12 bg-navy">
        <div className="container mx-auto text-center max-w-2xl">
          <h2 className="font-cormorant text-3xl md:text-5xl text-white mb-6">
            Sign up for my newsletter!
          </h2>
          <p className="text-white/80 mb-10">
            Stay updated on what's happening in the community and the chamber
          </p>
          <form
            id="newsletter-form"
            onSubmit={handleNewsletterSubmit}
            className="space-y-4"
          >
            <div id="form-fields" className="space-y-4">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full px-6 py-4 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full px-6 py-4 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <button
                type="submit"
                className="w-full bg-gold hover:bg-[#e09f0a] text-white font-bold px-12 py-4 text-sm tracking-wide rounded-md transition-colors"
              >
                SUBSCRIBE
              </button>
            </div>
            <div id="success-message" style={{ display: 'none' }} className="text-white text-xl font-medium">
              Thank you for signing up!
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default About;
